import { useQueryClient } from "@tanstack/react-query";
import { CocQuery } from "./coc.query";
import styled from "styled-components";

import moment from "moment";
import { useState } from "react";
import { useTheme } from "styled-components";
import { usePermissions } from "../../hooks/usePermissions";
import { DropDownMenu } from "@monolith-forensics/monolith-ui";
import { logActivity } from "../../api/index.js";
import { monolithMoment } from "../../utils/date-format";

import { ChevronDown, Pencil, TrashIcon } from "lucide-react";

import DestroyCocModal from "@/Features/ChainOfCustody/Modals/DestroyCocModal";
import InsertCocModal from "@/Features/ChainOfCustody/Modals/InsertCocModal";
import RecollectCocModal from "@/Features/ChainOfCustody/Modals/RecollectCocModal";
import ReturnToParentCocModal from "@/Features/ChainOfCustody/Modals/ReturnToParentCocModal";
import DeleteCocModal from "@/Features/ChainOfCustody/Modals/DeleteCocModal";
import EditCocModal from "@/Features/ChainOfCustody/Modals/EditCocModal";
import CheckoutCocModal from "@/Features/ChainOfCustody/Modals/CheckoutCocModal";
import MoveCocModal from "@/Features/ChainOfCustody/Modals/MoveItemCocModal";
import ReleaseCocModal from "@/Features/ChainOfCustody/Modals/ReleaseCocModal";

import type {
  CocRecord,
  ActionItem,
  ReportActionItem,
} from "@/Features/ChainOfCustody/Coc.types";
import { CocIntakeModal, Evidence } from "@/Features";
import EvidenceCocAPI from "@/api/evidence/coc.js";

interface CocContentProps {
  className?: string;
  evidenceInfo: Evidence;
}

interface ActionMenuProps {
  className?: string;
  evidenceInfo: Evidence;
  records: CocRecord[];
}

interface CocRecordProps {
  className?: string;
  evidenceInfo: Evidence;
  record: CocRecord;
}

interface DetailsProps {
  className?: string;
  item: CocRecord;
}

interface DetailItemProps {
  className?: string;
  label: string;
  value: React.ReactNode;
}

const ActionMenu = styled(
  ({ className, evidenceInfo, records }: ActionMenuProps) => {
    const theme: any = useTheme();
    const { currentUser, hasPermission } = usePermissions();
    const queryClient = useQueryClient();
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [showReleaseModal, setShowReleaseModal] = useState(false);
    const [showDestroyModal, setShowDestroyModal] = useState(false);
    const [showInsertRecordModal, setShowInsertRecordModal] = useState(false);
    const [showCheckoutItemModal, setShowCheckoutItemModal] = useState(false);
    const [showIntakeModal, setShowIntakeModal] = useState(false);
    const [showReturnToParentModal, setShowReturnToParentModal] =
      useState(false);
    const [showReCollectModal, setShowReCollectModal] = useState(false);

    const intakeExists = records.find((r: CocRecord) => r.type === "Intake");

    const cocActions: ActionItem[] = [
      {
        label: "Move Item",
        value: "move",
        onClick: () => setShowMoveModal(true),
        visible:
          records.length !== 0 &&
          evidenceInfo.location_id !== 1 && // not released
          evidenceInfo.location_id !== -1, // not destroyed
      },
      {
        label: "Checkout Item",
        value: "checkout",
        onClick: () => setShowCheckoutItemModal(true),
        visible:
          records.length !== 0 &&
          evidenceInfo.location_id !== 1 && // not released
          evidenceInfo.location_id !== -1 && // not destroyed
          evidenceInfo.location_id !== currentUser.location_id, // not current user's location
      },
      {
        label: "Return to Parent",
        value: "return",
        onClick: () => setShowReturnToParentModal(true),
        visible:
          records.length !== 0 &&
          evidenceInfo.is_parent !== 1 && // not a parent item
          evidenceInfo.location_name !== evidenceInfo.parent_name && // not at parent location
          evidenceInfo.location_id !== 1 && // not released
          evidenceInfo.location_id !== -1, // not destroyed
      },
      {
        label: "Release Item",
        value: "release",
        onClick: () => setShowReleaseModal(true),
        visible:
          records.length !== 0 &&
          evidenceInfo.location_id !== 1 && // not released
          evidenceInfo.location_id !== -1, // not destroyed
      },
      {
        label: "Destroy Item",
        value: "destroy",
        onClick: () => setShowDestroyModal(true),
        visible:
          records.length !== 0 &&
          evidenceInfo.location_id !== 1 && // not released
          evidenceInfo.location_id !== -1, // not destroyed
      },
      {
        label: "Enter Intake Details",
        value: "intake",
        onClick: () => setShowIntakeModal(true),
        visible:
          !intakeExists && // no intake exists
          evidenceInfo.location_id !== 1 && // not released
          evidenceInfo.location_id !== -1, // not destroyed
      },
      {
        label: "Re-collect Item",
        value: "recollect",
        onClick: () => setShowReCollectModal(true),
        visible: records.length !== 0 && evidenceInfo.location_id === 1, // must be released
      },
      {
        label: "Insert Record",
        value: "insert",
        onClick: () => setShowInsertRecordModal(true),
        visible: records.length !== 0 && currentUser.role_id === 1, // must be super admin
      },
    ].filter((a) => a.visible);

    const reportActions: ReportActionItem[] = [
      {
        label: "Chain of Custody Report",
        value: "coc_report",
        onClick: async () => {
          const records = await EvidenceCocAPI.getCocRecords({
            evidence_id: evidenceInfo?.evidence_id as any,
          });
          EvidenceCocAPI.createEvidenceCocPDF({
            evidence: {
              ...evidenceInfo,
              currenUserFullName: currentUser.full_name,
            },
            cocData: records,
            options: { suppCoc: false },
          }).then(() => {
            logActivity(
              evidenceInfo?.case_id,
              currentUser.user_id,
              `Generated PDF COC Report for evidence item ${evidenceInfo?.evidence_number}.`
            );
          });
        },
      },
      {
        label: "Manual Chain of Custody",
        value: "manual_coc",
        onClick: async () => {
          const records = await EvidenceCocAPI.getCocRecords({
            evidence_id: evidenceInfo?.evidence_id as any,
          });
          EvidenceCocAPI.createEvidenceCocPDF({
            evidence: {
              ...evidenceInfo,
              currenUserFullName: currentUser.full_name,
            },
            cocData: records,
            options: { suppCoc: true },
          }).then(() => {
            logActivity(
              evidenceInfo?.case_id,
              currentUser.user_id,
              `Generated PDF COC Report for evidence item ${evidenceInfo?.evidence_number}.`
            );
          });
        },
      },
      {
        label: "Blank Intake Form",
        value: "intake_form",
        onClick: () => {
          EvidenceCocAPI.createEvidenceIntakePDF({
            evidence: {
              ...evidenceInfo,
              currenUserFullName: currentUser.full_name,
            },
          }).then(() => {
            logActivity(
              evidenceInfo?.case_id,
              currentUser.user_id,
              `Generated PDF Intake Form for evidence item ${evidenceInfo?.evidence_number}.`
            );
          });
        },
      },
      {
        label: "Blank Release Form",
        value: "release_form",
        onClick: () => {
          EvidenceCocAPI.createEvidenceReleasePDF({
            evidence: {
              ...evidenceInfo,
              currenUserFullName: currentUser.full_name,
            },
          }).then(() => {
            logActivity(
              evidenceInfo?.case_id,
              currentUser.user_id,
              `Generated PDF Release Form for evidence item ${evidenceInfo?.evidence_number}.`
            );
          });
        },
      },
    ];

    const refetchCocData = () => {
      queryClient.refetchQueries({
        queryKey: [
          "evidence:cocData",
          { evidence_id: evidenceInfo.evidence_id },
        ],
      });

      queryClient.refetchQueries({
        queryKey: ["evidence", "list", { uuid: evidenceInfo.uuid }],
      });
    };

    const onMove = () => refetchCocData();
    const onRelease = () => refetchCocData();
    const onDestroy = () => refetchCocData();
    const onInsertRecord = () => refetchCocData();
    const onCheckoutItem = () => refetchCocData();
    const onIntake = () => refetchCocData();
    const onReturnItemToParent = () => refetchCocData();
    const onReCollect = () => refetchCocData();

    return (
      <div className={className}>
        {hasPermission() && (
          <DropDownMenu
            variant="subtle"
            size="xs"
            arrow
            data={cocActions}
            dropDownProps={{
              style: { width: 175, maxWidth: 400 },
            }}
            buttonProps={{
              size: "xxs",
              style: {
                color: theme.palette.text.primary,
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${theme.palette.divider}`,
              },
            }}
          >
            COC Actions
          </DropDownMenu>
        )}
        <DropDownMenu
          variant="subtle"
          size="xs"
          arrow
          data={reportActions}
          dropDownProps={{
            style: { width: 175, maxWidth: 400 },
          }}
          buttonProps={{
            size: "xxs",
            style: {
              color: theme.palette.text.primary,
              backgroundColor: theme.palette.background.default,
              border: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          COC Documents
        </DropDownMenu>
        {showMoveModal && (
          <MoveCocModal
            show={showMoveModal}
            onClose={() => setShowMoveModal(false)}
            onSubmit={onMove}
            defaultInfo={evidenceInfo}
          />
        )}
        {showReleaseModal && (
          <ReleaseCocModal
            show={showReleaseModal}
            onClose={() => setShowReleaseModal(false)}
            onSubmit={onRelease}
            defaultInfo={evidenceInfo}
          />
        )}
        {showDestroyModal && (
          <DestroyCocModal
            show={showDestroyModal}
            onClose={() => setShowDestroyModal(false)}
            onSubmit={onDestroy}
            defaultInfo={evidenceInfo}
          />
        )}
        {showInsertRecordModal && (
          <InsertCocModal
            show={showInsertRecordModal}
            onClose={() => setShowInsertRecordModal(false)}
            onSubmit={onInsertRecord}
            defaultInfo={evidenceInfo}
          />
        )}
        {showCheckoutItemModal && (
          <CheckoutCocModal
            show={showCheckoutItemModal}
            onClose={() => setShowCheckoutItemModal(false)}
            onSubmit={onCheckoutItem}
            defaultInfo={evidenceInfo}
          />
        )}
        {showIntakeModal && (
          <CocIntakeModal
            show={showIntakeModal}
            onClose={() => setShowIntakeModal(false)}
            onSubmit={onIntake}
            evidence_id={evidenceInfo.evidence_id}
          />
        )}
        {showReturnToParentModal && (
          <ReturnToParentCocModal
            show={showReturnToParentModal}
            onClose={() => setShowReturnToParentModal(false)}
            onSubmit={onReturnItemToParent}
            defaultInfo={evidenceInfo}
          />
        )}
        {showReCollectModal && (
          <RecollectCocModal
            show={showReCollectModal}
            onClose={() => setShowReCollectModal(false)}
            onSubmit={onReCollect}
            defaultInfo={evidenceInfo}
          />
        )}
      </div>
    );
  }
)`
  display: flex;
  flex-direction: row;
  gap: 5px;
`;

const CocRecordComponent = styled(
  ({ className, evidenceInfo, record }: CocRecordProps) => {
    const [open, setOpen] = useState(false);
    const handleClick = () => setOpen(!open);
    return (
      <div className={className}>
        <div className="outer-container">
          <div>
            <div className="record-type">{record.type}</div>
            <div className="record-timestamp">
              <div
                style={{ marginRight: 5 }}
                title={monolithMoment({
                  timestamp: record.timestamp,
                  includeTime: true,
                })}
              >
                {moment.utc(record.timestamp).fromNow()}
              </div>
            </div>
            {!open && (
              <div className="coc-preview-data">
                <div className="coc-data">
                  Custody From:{" "}
                  <span title={record.custody_from}>{record.custody_from}</span>
                </div>
                <div className="coc-data">
                  Custody To:{" "}
                  <span title={record.custody_to}>{record.custody_to}</span>
                </div>
                <div className="coc-data">
                  Description:{" "}
                  <span title={record.reason}>{record.reason}</span>
                </div>
              </div>
            )}
          </div>
          <div className="view-btn" onClick={() => handleClick()}>
            <div className="view-btn-title">View Details</div>
            <ChevronDown
              size={15}
              style={{
                //rotate
                transform: !open ? "rotate(-90deg)" : "rotate(0deg)",
                transition: "transform 0.3s ease-in-out",
              }}
            />
          </div>
        </div>
        {open && <Details item={record} />}
      </div>
    );
  }
)`
  font-size: 14px;
  padding: 15px;
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 5px;

  & .outer-container {
    display: flex;
  }

  & .record-type {
    font-size: 16px;
    font-weight: 600;
    margin-right: 10px;
  }

  & .record-timestamp {
    display: flex;
    align-items: center;
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 12px;
    :hover {
      color: ${({ theme }) => theme.palette.text.primary};
      cursor: pointer;
    }
  }

  & .coc-preview-data {
    display: flex;
    flex-wrap: wrap;
    color: ${({ theme }) => theme.palette.text.secondary};
    font-size: 11px;
    margin-top: 10px;
  }

  & .coc-data {
    margin-right: 15px;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  & .coc-data span {
    color: ${({ theme }) => theme.palette.text.primary};
  }

  & .view-btn {
    cursor: pointer;
    font-size: 10px;
    color: ${({ theme }) => theme.palette.text.secondary};
    display: flex;
    align-items: center;
    margin-left: auto;
    svg {
      font-size: 15px;
    }
    &:hover,
    &:hover svg,
    &:hover .view-btn-title {
      color: ${({ theme }) => theme.palette.primary.main};
      text-decoration: underline;
    }
  }
  & .view-btn-title {
    margin-right: 5px;
  }
`;

const Details = styled(({ className, item }: DetailsProps) => {
  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModel, setShowDeleteModel] = useState(false);
  const queryClient = useQueryClient();
  const theme: any = useTheme();

  const onEdit = () => {
    queryClient.refetchQueries({
      queryKey: ["evidence:cocData", { evidence_id: item.evidence_id }],
    });
  };

  let signature = item?.signature || {};

  return (
    <div className={className}>
      <div style={{ display: "flex", width: "fit-content" }}>
        {hasPermission(MONOLITH_PERMISSIONS.COC_UPDATE) && (
          <div
            className="action-menu-item"
            onClick={() => setShowEditModal(true)}
            style={{ marginBottom: 10 }}
          >
            <Pencil size={13} style={{ color: theme.palette.primary.main }} />
            <div className="action-menu-label">Edit</div>
          </div>
        )}
        <EditCocModal
          show={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={onEdit}
          defaultInfo={item}
        />
        {hasPermission(MONOLITH_PERMISSIONS.COC_DELETE) && (
          <div
            className="action-menu-item"
            onClick={() => setShowDeleteModel(true)}
            style={{ marginBottom: 10 }}
          >
            <TrashIcon size={13} style={{ color: theme.palette.error.main }} />
            <div className="action-menu-label">Delete</div>
          </div>
        )}
        <DeleteCocModal
          show={showDeleteModel}
          onClose={() => setShowDeleteModel(false)}
          onSubmit={onEdit}
          onCancel={() => setShowDeleteModel(false)}
          defaultInfo={item}
        />
      </div>
      <DetailItem label="Type" value={item.type} />
      <DetailItem
        label="Timestamp"
        value={monolithMoment({
          timestamp: item.timestamp,
          includeTime: true,
          includeZone: true,
        })}
      />
      <DetailItem
        label="Custody From"
        value={
          <div>
            <div>{item.custody_from}</div>
            {signature?.custody_from_sig && (
              <div style={{ marginTop: 20, marginBottom: 20 }}>
                <img
                  src={signature.custody_from_sig.signature}
                  style={{
                    maxHeight: 30,
                    filter: theme.name === "DARK" ? "invert(1)" : "unset",
                  }}
                  alt="signature"
                />
              </div>
            )}
          </div>
        }
      />
      <DetailItem
        label="Custody To"
        value={
          <div>
            <div>{item.custody_to}</div>
            {signature?.custody_to_sig && (
              <div style={{ marginTop: 20, marginBottom: 20 }}>
                <img
                  src={signature.custody_to_sig.signature}
                  style={{
                    maxHeight: 30,
                    filter: theme.name === "DARK" ? "invert(1)" : "unset",
                  }}
                  alt="signature"
                />
              </div>
            )}
          </div>
        }
      />
      <DetailItem label="Description" value={item.reason} />
    </div>
  );
})`
  margin-top: 15px;
`;

const DetailItem = styled(({ className, label, value }: DetailItemProps) => {
  return (
    <div className={className}>
      <div className="request-detail-label">{label}</div>
      <div className="request-detail-value">{value}</div>
    </div>
  );
})`
  display: flex;
  margin-bottom: 8px;
  font-size: 12px;
  .request-detail-label {
    color: ${(props) => props.theme.palette.text.secondary};
    margin-right: 10px;
    min-width: 100px;
  }
  .request-detail-value {
    color: ${(props) => props.theme.palette.text.primary};
    white-space: pre-line;
  }
`;

const StyledCocContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1 1 auto;
  overflow-y: auto;
`;

const StyledCocRecordsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1 1 auto;
  overflow-y: auto;
  height: 0px;
  padding-right: 10px;
`;

export const CocContent: React.FC<CocContentProps> = ({ evidenceInfo }) => {
  const { data } = CocQuery.useGetCocRecords(evidenceInfo.evidence_id);

  if (!data) return null;

  const sortedData = [...data].sort((a: CocRecord, b: CocRecord) => {
    if (a.timestamp > b.timestamp) return 1;
    if (a.timestamp < b.timestamp) return -1;
    return 0;
  });

  return (
    <StyledCocContent>
      <ActionMenu evidenceInfo={evidenceInfo} records={sortedData} />
      <StyledCocRecordsContainer>
        {sortedData.length === 0 && (
          <div style={{ marginTop: 10, marginBottom: 20 }}>
            No chain of custody records
          </div>
        )}
        {sortedData.map((record: CocRecord) => {
          return (
            <CocRecordComponent
              key={record.coc_id}
              evidenceInfo={evidenceInfo}
              record={record}
            />
          );
        })}
      </StyledCocRecordsContainer>
    </StyledCocContent>
  );
};
